/**
 * AdminSecurity (v4.7.67)
 *
 * Self-service security panel for the authenticated admin. Hosts the TOTP
 * (authenticator-app) MFA management UI inside the Admin Dashboard so admins
 * can enroll / disable / regenerate backup codes for their own account
 * without leaving the admin surface.
 *
 * The underlying TotpMfaPanel already calls /api/auth/mfa/{status,setup,
 * verify-setup,disable,regenerate-backup-codes}; all of those endpoints
 * derive the userId from withSessionAuth's req.user.id, so the panel works
 * unchanged for any authenticated user — including admins whose users.id
 * row carries is_admin=true.
 *
 * We extract the numeric users.id and email from the admin's currentUser
 * object (database_user.id + database_user.email, with fall-backs to
 * top-level fields) before mounting the panel. If extraction fails we render
 * a clear error rather than mount the panel with an undefined userId.
 */
import React from 'react'
import { TotpMfaPanel } from '../TotpMfaPanel'
import { extractNumericId } from '../../lib/userIdExtractor'

interface Props { currentUser?: any }

const AdminSecurity: React.FC<Props> = ({ currentUser }) => {
  const numericId =
    extractNumericId(currentUser?.database_user?.id) ??
    extractNumericId(currentUser?.user_metadata?.user_id) ??
    extractNumericId(currentUser?.id)

  const email: string | undefined =
    currentUser?.database_user?.email ||
    currentUser?.email ||
    currentUser?.adminUser?.email

  return (
    <div>
      <h2 style={{ fontSize: '24px', color: '#F59E0B', marginBottom: '8px', fontWeight: 600 }}>
        🔑 My Security
      </h2>
      <p style={{ color: '#cccccc', fontSize: '14px', marginBottom: '24px' }}>
        Manage two-factor authentication (TOTP) for your admin account.
        Authenticator-app MFA is required to use the <strong>User Assistance</strong>
        impersonation tool.
      </p>

      {(!numericId || !email) ? (
        <div style={{
          padding: '16px',
          borderRadius: '8px',
          border: '1px solid #5a2a2a',
          backgroundColor: '#2a1212',
          color: '#f5b3b3',
          fontSize: '14px'
        }}>
          ⚠️ Could not resolve your account identifier for MFA management.
          Please sign out and sign back in, then try again. If this persists,
          contact a system administrator.
        </div>
      ) : (
        <TotpMfaPanel userId={numericId} email={email} />
      )}
    </div>
  )
}

export default AdminSecurity
